// Typed fetch wrappers for the marketing-engine Hono API.
// Same-origin in dev (Vite proxies /crm/api → api.luzgridtech.com).
// Cross-origin in prod (preview.api.* → api.luzgridtech.com), so credentials are required.

import type {
  InboxFolder,
  InboxThreadCard,
  InboxThreadMessages,
  MessageBody,
  Notification,
} from "@/types/api";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message?: string,
  ) {
    // Prefer the worker's `error` field when present so the UI surfaces
    // something specific ("feature 'inbox_draft' is disabled") instead of
    // a useless "API 500". Falls through to the explicit message arg or
    // the generic shape if the body is missing/empty.
    const fromBody = (body && typeof body === "object" && "error" in body && typeof (body as { error: unknown }).error === "string")
      ? (body as { error: string }).error
      : null;
    super(message ?? fromBody ?? `API ${status}`);
  }
  /** Worker-side status string (e.g. "feature_disabled", "budget_exceeded"). */
  get aiStatus(): string | null {
    if (this.body && typeof this.body === "object" && "status" in this.body && typeof (this.body as { status: unknown }).status === "string") {
      return (this.body as { status: string }).status;
    }
    return null;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  // 401 → bounce to OAuth start. Return a never-resolving promise so every
  // caller (including authApi.ping) stays pending while the browser navigates.
  // This keeps React Query in isLoading state → BootSplash stays on screen
  // instead of flashing the authenticated shell for a frame.
  if (res.status === 401) {
    const fullReturn = window.location.origin + window.location.pathname + window.location.search;
    const ret = encodeURIComponent(fullReturn);
    window.location.replace(`${API_BASE}/crm/auth/login?return_to=${ret}`);
    return new Promise<never>(() => {});
  }

  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const body = isJson ? await res.json() : await res.text();

  if (!res.ok) throw new ApiError(res.status, body);

  // Defensive: if Pages' SPA fallback ever serves index.html for an API path
  // (it shouldn't — _redirects proxies /crm/api/* to the worker — but if the
  // proxy is misconfigured we'd silently return HTML typed as T and crash
  // downstream). Throw a clean error instead so the auth gate / query layer
  // can catch it.
  if (!isJson) {
    throw new ApiError(res.status, body, `expected JSON, got ${contentType || "(none)"}`);
  }

  return body as T;
}

// ─── Inbox ────────────────────────────────────────────────────────────

interface ReplyPayload {
  messageId?: string;
  to?: string[];
  cc?: string[];
  body: string;
  subject?: string;
  /** Mailbox to send as — one of the entries in /crm/api/inbox/mailboxes. */
  fromMailbox?: string;
  /** Per-response signature toggle. Defaults to true server-side. */
  includeSignature?: boolean;
  /** Contact composer also threads back to the contact-detail page invalidation. */
  mode?: "reply" | "reply_all" | "forward" | "internal_note";
  conversationId?: string;
}

export const aiApi = {
  // Backend returns AiCallResult (`{ ok, content, status, ... }`),
  // NOT `{ insight }`. Earlier typing predated the real shape and
  // silently mapped to undefined when callers read `.insight`.
  insight(contactId: string, opts: { force?: boolean } = {}): Promise<{ ok: boolean; content?: string; status?: string; error?: string; from_cache?: boolean; generated_at?: string }> {
    const qs = opts.force ? "?force=1" : "";
    return request(`/crm/api/contacts/${encodeURIComponent(contactId)}/insight${qs}`, { method: "POST" });
  },
  coach(dealId: string, opts: { force?: boolean } = {}): Promise<{ ok: boolean; content?: string; status?: string; error?: string; from_cache?: boolean; generated_at?: string }> {
    const qs = opts.force ? "?force=1" : "";
    return request(`/crm/api/deals/${encodeURIComponent(dealId)}/coach${qs}`, { method: "POST" });
  },
  draftReply(threadId: string, context?: string): Promise<{ draft: string }> {
    return request(`/crm/api/threads/${encodeURIComponent(threadId)}/draft`, {
      method: "POST",
      body: JSON.stringify({ context }),
    });
  },
  // First-touch draft for a contact with no existing thread — used by the
  // contact-detail "New email" composer for fresh website-form leads.
  draftForContact(contactId: string, context?: string): Promise<{ draft: string }> {
    return request(`/crm/api/contacts/${encodeURIComponent(contactId)}/draft-email`, {
      method: "POST",
      body: JSON.stringify({ context }),
    });
  },
  // Backend returns an AiCallResult (`{ ok, content, status, ... }`),
  // plus `from_cache` + `generated_at` for cache hits. Earlier this was
  // typed as `{ brief: string }` which never matched the real payload.
  morningBrief(opts: { force?: boolean } = {}): Promise<{ ok: boolean; content?: string; status?: string; error?: string; from_cache?: boolean; generated_at?: string }> {
    const qs = opts.force ? "?force=1" : "";
    return request(`/crm/api/ai/morning-brief${qs}`, { method: "POST" });
  },
  campaignInsights(): Promise<{ insights: string }> {
    return request(`/crm/api/ai/campaign-insights`, { method: "POST" });
  },
};

export const inboxApi = {
  threads(folder: InboxFolder = "Inbox"): Promise<{ threads: InboxThreadCard[] }> {
    return request(`/crm/api/inbox/threads?folder=${encodeURIComponent(folder)}`);
  },

  threadMessages(conversationId: string): Promise<InboxThreadMessages> {
    return request(`/crm/api/inbox/threads/${encodeURIComponent(conversationId)}/messages`);
  },

  messageBody(graphMessageId: string): Promise<MessageBody> {
    return request(`/crm/api/messages/${encodeURIComponent(graphMessageId)}/body`);
  },

  mirrorNow(force = false): Promise<{ ok: boolean; folders?: unknown[] }> {
    return request(`/crm/api/inbox/mirror-now${force ? "?force=1" : ""}`, { method: "POST" });
  },

  reply(payload: ReplyPayload): Promise<{ ok: true; sentFrom: string }> {
    return request(`/crm/api/inbox/reply`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  draft(payload: ReplyPayload): Promise<{ ok: true }> {
    return request(`/crm/api/inbox/draft`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  markRead(threadId: string): Promise<{ ok: true }> {
    return request(`/crm/api/threads/${encodeURIComponent(threadId)}/read`, { method: "POST" });
  },

  /**
   * List the mailboxes the current user can send as.
   *
   * Pass a contactId when composing on a contact-scoped surface — the
   * backend uses it to compute an owner-aware default: if the contact is
   * owned by the signed-in user, the default flips from outreach@ to the
   * user's own mailbox (e.g. jose@). Without contactId, default falls
   * back to the shared mailbox.
   */
  mailboxes(contactId?: string): Promise<{ mailboxes: string[]; default: string }> {
    const qs = contactId ? `?contact_id=${encodeURIComponent(contactId)}` : "";
    return request(`/crm/api/inbox/mailboxes${qs}`);
  },
};

// Inbox snippets — reusable reply text, managed from the composer's
// "Manage snippets…" button.
export interface InboxSnippet {
  id: string;
  name: string;
  body: string;
  is_active: boolean;
  updated_at: string;
}
export const snippetsApi = {
  list(): Promise<{ snippets: InboxSnippet[] }> {
    return request(`/crm/api/inbox/snippets`);
  },
  create(name: string, body: string): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/inbox/snippets`, {
      method: "POST",
      body: JSON.stringify({ name, body }),
    });
  },
  update(id: string, patch: Partial<{ name: string; body: string; is_active: boolean }>): Promise<{ ok: true }> {
    return request(`/crm/api/inbox/snippets/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  delete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/inbox/snippets/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};

// ─── Notifications ────────────────────────────────────────────────────

// The worker's apiNotifications aggregates synthetic items (hot replies,
// overdue tasks, expiring contracts, AI budget) instead of reading a single
// notifications table — see src/crm/routes/notifications.ts.
// Items don't have row IDs in a single table, so "mark as read" is a UI-only
// concept (the items naturally drop off as their underlying state changes).
export const notificationsApi = {
  async list(): Promise<{ notifications: Notification[] }> {
    const raw = await request<{
      ok?: boolean;
      items?: Array<{
        id: string;
        kind: string;
        title: string;
        detail?: string;
        href?: string;
        at?: string;
        unread?: boolean;
      }>;
    }>(`/crm/api/notifications`);
    const items = raw.items ?? [];
    return {
      notifications: items.map((it) => ({
        id: it.id,
        kind: it.kind,
        title: it.title,
        description: it.detail ?? null,
        action_url: it.href ?? null,
        read_at: it.unread ? null : new Date().toISOString(),
        created_at: it.at ?? new Date().toISOString(),
      })),
    };
  },
  // markRead is now a no-op alias for dismiss; keeping the method so existing
  // call sites don't break, but it physically removes the item.
  async markRead(id: string): Promise<{ ok: true }> {
    await notificationsApi.dismiss(id);
    return { ok: true };
  },
  dismiss(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/notifications/dismiss/${encodeURIComponent(id)}`, { method: "POST" });
  },
  dismissAll(): Promise<{ ok: true; dismissed: number }> {
    return request(`/crm/api/notifications/dismiss-all`, { method: "POST" });
  },
  // Restore everything I previously dismissed — escape hatch when the user
  // wants the bell back to "show all current items" without waiting for
  // re-fires. Per-user, no audit log.
  clearDismissed(): Promise<{ ok: true; cleared: number }> {
    return request(`/crm/api/notifications/clear-dismissed`, { method: "POST" });
  },
};

// Email templates (admin) — v1 only contact_form_autoresponder.
export interface EmailTemplate {
  id: string;
  slug: string;
  name: string;
  subject: string;
  preheader: string | null;
  body_markdown: string | null;
  body_html: string | null;
  variables_json: string | null;
}
export interface TemplateVariable {
  token: string;
  description: string;
}
export const emailTemplatesApi = {
  get(slug: string): Promise<{
    template: EmailTemplate;
    defaults: { subject: string; preheader: string; body_markdown: string };
    available_variables: TemplateVariable[];
  }> {
    return request(`/crm/api/admin/email-templates/${encodeURIComponent(slug)}`);
  },
  put(slug: string, body: Partial<EmailTemplate> & { include_signature?: boolean }): Promise<{ ok: true }> {
    return request(`/crm/api/admin/email-templates/${encodeURIComponent(slug)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
  reset(slug: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/email-templates/${encodeURIComponent(slug)}/reset`, { method: "POST" });
  },
};

// Per-user email signature (used by templates with Include signature).
export const signatureApi = {
  get(): Promise<{ signature_html: string }> {
    return request(`/crm/api/me/signature`);
  },
  put(signature_html: string): Promise<{ ok: true }> {
    return request(`/crm/api/me/signature`, {
      method: "PUT",
      body: JSON.stringify({ signature_html }),
    });
  },
};

// Persistent request error log (admin-only). Captures 5xx + uncaught
// across every entry point. 30-day retention.
export interface RequestLogRow {
  id: string;
  occurred_at: string;
  expires_at: string;
  request_method: string;
  request_path: string;
  request_query: string | null;
  cf_ray: string | null;
  cf_country: string | null;
  user_agent: string | null;
  client_ip: string | null;
  status: number;
  error_kind: string | null;
  error_message: string | null;
  stack: string | null;
  request_body_preview: string | null;
}
export const requestLogApi = {
  list(params: { limit?: number; offset?: number; min_status?: number; path_like?: string } = {}): Promise<{ items: RequestLogRow[]; total: number }> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    }
    const q = qs.toString();
    return request(`/crm/api/admin/request-log${q ? `?${q}` : ""}`);
  },
};

// Recycle bin (admin-only): list / restore / purge soft-deleted contacts.
export interface RecycledContact {
  contact_id: string;
  email: string;
  full_name: string | null;
  deleted_at: string;
  deleted_by_user_id: string | null;
  deleted_by_name: string | null;
  expires_at: string;
}
export const recycledContactsApi = {
  list(): Promise<{ items: RecycledContact[] }> {
    return request(`/crm/api/admin/recycled-contacts`);
  },
  restore(id: string): Promise<{
    ok: true;
    restored_contact_id: string;
    notes: number;
    tasks: number;
    activities: number;
    relinked_deals: number;
    relinked_contracts: number;
    kv_keys_restored: number;
  }> {
    return request(`/crm/api/admin/recycled-contacts/${encodeURIComponent(id)}/restore`, { method: "POST" });
  },
  purge(id: string): Promise<{ ok: true; purged: number }> {
    return request(`/crm/api/admin/recycled-contacts/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};

// (trashApi for crm_trash already defined further below alongside the
// cascade-delete API — no duplicate declaration here.)

// Per-user notification preferences. Bell-* toggles control visibility of
// each kind in the topbar bell panel (added 2026-05-19). The email toggle
// controls the hot-lead-inbound fan-out email — separate channel.
export interface NotificationPrefs {
  hot_lead_email: boolean;
  bell_hot_lead: boolean;
  bell_subscriber: boolean;
  bell_email_queued: boolean;
  bell_reply: boolean;
  bell_task_overdue: boolean;
  bell_contract_expiring: boolean;
  bell_email_opened: boolean;
  bell_email_clicked: boolean;
  bell_ai_budget: boolean;
}
export const notificationPrefsApi = {
  get(): Promise<{ prefs: NotificationPrefs; defaults: NotificationPrefs }> {
    return request(`/crm/api/me/notification-prefs`);
  },
  patch(patch: Partial<NotificationPrefs>): Promise<{ ok: true; prefs: NotificationPrefs }> {
    return request(`/crm/api/me/notification-prefs`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
};

// ─── Contacts ─────────────────────────────────────────────────────────

import type {
  ContactsListResponse,
  ContactDetailResponse,
  CrmContact,
  CrmNote,
  CrmTask,
  CrmCompany,
  CrmDeal,
  CrmPipeline,
  CrmContract,
  CrmContractTemplate,
  CrmContractClause,
  CrmContractVariable,
  CrmSow,
  CrmCampaign,
  CrmSequence,
  CrmEmailTemplate,
  ReportsSummary,
  CrmForm,
  CrmSuppression,
  CrmPrivacyRequest,
  CrmUser,
  CrmAuditEntry,
  CrmCostAlert,
  CrmAiSettings,
  CrmApiKey,
  CrmBranding,
  ContactSequenceEnrollment,
} from "@/types/api";

const qs = (params: Record<string, string | number | undefined | null>) => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
};

/** Query params accepted by GET /crm/api/contacts. Multi-select enums may
 *  pass as `string[]` — list() serializes them comma-joined before sending. */
export interface ContactsListParams {
  q?: string;
  vertical?: string | string[];
  lifecycle?: string | string[];
  engagement?: string | string[];
  score?: string | string[];
  owner?: string;
  /** Server-side: `mine=true` resolves owner_user_id = signed-in user. */
  mine?: boolean;
  country?: string;
  state?: string;
  has_phone?: boolean;
  has_hubspot_id?: boolean;
  is_vip?: boolean;
  modified_since?: string;
  sort?: string;
  dir?: "asc" | "desc";
  /** Legacy alias preserved for the existing SavedViewsPicker plumbing. */
  status?: string;
  limit?: number;
  offset?: number;
}

export const contactsApi = {
  // The pre-existing worker returns { rows, total } from listContacts() — we
  // remap to { contacts, total } so the React side has a consistent shape with
  // companies/deals/contracts. Don't change the worker — classic CRM still
  // reads it as `rows`.
  async list(params: ContactsListParams = {}): Promise<ContactsListResponse> {
    // Multi-select enums (vertical / lifecycle / engagement) arrive as
    // string[] from the FilterBar; serialize CSV before hitting qs() so the
    // worker side keeps treating them as a single string param.
    const flat: Record<string, string | number | undefined> = {};
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === "") continue;
      if (Array.isArray(v)) flat[k] = v.length === 0 ? undefined : v.join(",");
      else if (typeof v === "boolean") flat[k] = v ? "true" : "false";
      else flat[k] = v as string | number;
    }
    const raw = await request<{ rows?: unknown[]; contacts?: unknown[]; total: number; pages?: number }>(
      `/crm/api/contacts${qs(flat)}`,
    );
    return {
      contacts: ((raw.rows ?? raw.contacts ?? []) as ContactsListResponse["contacts"]),
      total: raw.total,
      pages: raw.pages,
    };
  },
  // Use the /full endpoint which bundles notes/tasks/activities in one shot.
  get(id: string): Promise<ContactDetailResponse> {
    return request(`/crm/api/contacts/${encodeURIComponent(id)}/full`);
  },
  // Per-contact sequence enrollments (KV-backed) for the pizza tracker.
  sequences(id: string): Promise<{ enrollments: ContactSequenceEnrollment[] }> {
    return request(`/crm/api/contacts/${encodeURIComponent(id)}/sequences`);
  },
  create(body: Partial<CrmContact>): Promise<CrmContact> {
    return request(`/crm/api/contacts`, { method: "POST", body: JSON.stringify(body) });
  },
  update(id: string, body: Partial<CrmContact>): Promise<CrmContact> {
    return request(`/crm/api/contacts/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  // Hard delete — cascades D1 related rows (notes/tasks/activities/forms/
  // memberships/runs) and purges the cold-sequence KV state. Use archive
  // for soft hide; this is for tear-down (testing flows + GDPR erasure).
  delete(id: string, opts: { force?: boolean } = {}): Promise<{ ok: true; deleted_contact_id: string; purged_email: string | null; kv_overrides_purged: number }> {
    const qs = opts.force ? "?force=1" : "";
    return request(`/crm/api/contacts/${encodeURIComponent(id)}${qs}`, { method: "DELETE" });
  },
  reply(id: string, payload: { body: string; subject?: string; thread_id?: string; to?: string; from_mailbox?: string; include_signature?: boolean }): Promise<{ ok: true; thread_id: string; sentFrom: string }> {
    return request(`/crm/api/contacts/${encodeURIComponent(id)}/reply`, { method: "POST", body: JSON.stringify(payload) });
  },
  addNote(id: string, body: string, isPinned = false): Promise<CrmNote> {
    // Worker expects { body_markdown, is_pinned }, NOT { body }.
    return request(`/crm/api/contacts/${encodeURIComponent(id)}/notes`, {
      method: "POST",
      body: JSON.stringify({ body_markdown: body, is_pinned: isPinned }),
    });
  },
  togglePin(noteId: string): Promise<{ ok: true }> {
    return request(`/crm/api/notes/${encodeURIComponent(noteId)}/pin`, { method: "POST" });
  },
  deleteNote(noteId: string): Promise<{ ok: true }> {
    return request(`/crm/api/notes/${encodeURIComponent(noteId)}`, { method: "DELETE" });
  },
  addTask(id: string, body: Partial<CrmTask>): Promise<CrmTask> {
    return request(`/crm/api/contacts/${encodeURIComponent(id)}/tasks`, { method: "POST", body: JSON.stringify(body) });
  },
  // Alias retained for backwards-compat with older code paths. Prefer
  // aiApi.insight(id) — same endpoint, correct return type.
  generateInsight(id: string): Promise<{ ok: boolean; content?: string; status?: string; error?: string }> {
    return request(`/crm/api/contacts/${encodeURIComponent(id)}/insight`, { method: "POST" });
  },
  // Comments this contact has authored on their client portal projects
  // (project-level Messages tab + per-task / per-file replies). Read-only:
  // staff respond from the project view, not the contact view.
  portalMessages(id: string): Promise<{
    messages: Array<{
      id: string;
      project_id: string;
      parent_type: "project" | "task" | "file";
      parent_id: string | null;
      body_markdown: string;
      created_at: string;
      edited_at: string | null;
      project_name: string | null;
      project_slug: string | null;
      parent_label: string | null;
    }>;
  }> {
    return request(`/crm/api/contacts/${encodeURIComponent(id)}/portal-messages`);
  },
  bulkArchive(ids: string[], archived = true): Promise<{ ok: true; updated: number }> {
    return request(`/crm/api/contacts/bulk/archive`, {
      method: "POST",
      body: JSON.stringify({ ids, archived }),
    });
  },
  bulkAddToList(ids: string[], listId: string): Promise<{ ok: true; added: number }> {
    return request(`/crm/api/contacts/bulk/add-to-list`, {
      method: "POST",
      body: JSON.stringify({ ids, list_id: listId }),
    });
  },
  bulkEnroll(ids: string[], workflowId: string): Promise<{ ok: true; enrolled: number; errors: { id: string; error: string }[] }> {
    return request(`/crm/api/contacts/bulk/enroll`, {
      method: "POST",
      body: JSON.stringify({ ids, workflow_id: workflowId }),
    });
  },
};

export const contractsBulkApi = {
  trash(ids: string[]): Promise<{ ok: true; updated: number }> {
    return request(`/crm/api/contracts/bulk/trash`, {
      method: "POST",
      body: JSON.stringify({ ids }),
    });
  },
  restore(ids: string[]): Promise<{ ok: true; updated: number }> {
    return request(`/crm/api/contracts/bulk/trash`, {
      method: "POST",
      body: JSON.stringify({ ids, restore: true }),
    });
  },
};

// ─── Companies ────────────────────────────────────────────────────────

/** Query params accepted by GET /crm/api/companies. */
export interface CompaniesListParams {
  q?: string;
  vertical?: string | string[];
  industry?: string | string[];
  /** Migration 0066 — filter to subcontractor / vendor companies. */
  entity_type?: "client" | "subcontractor" | "vendor";
  company_size_band?: string | string[];
  lifecycle_stage?: string | string[];
  country?: string;
  state?: string;
  health_bucket?: string | string[];
  is_target_account?: boolean;
  is_hispanic_owned?: boolean;
  employee_count_min?: number;
  employee_count_max?: number;
  arr_min?: number;
  arr_max?: number;
  mrr_min?: number;
  mrr_max?: number;
  revenue_min?: number;
  revenue_max?: number;
  has_arr?: boolean;
  has_mrr?: boolean;
  modified_since?: string;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export const companiesApi = {
  list(params: CompaniesListParams = {}): Promise<{ companies: CrmCompany[]; total: number }> {
    // Same arrays-as-CSV / booleans-as-strings flattening pattern as
    // contactsApi.list. Centralized so each call site stays declarative.
    const flat: Record<string, string | number | undefined> = {};
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === "") continue;
      if (Array.isArray(v)) flat[k] = v.length === 0 ? undefined : v.join(",");
      else if (typeof v === "boolean") flat[k] = v ? "true" : "false";
      else flat[k] = v as string | number;
    }
    return request(`/crm/api/companies${qs(flat)}`);
  },
  get(id: string): Promise<CrmCompany> {
    return request(`/crm/api/companies/${encodeURIComponent(id)}`);
  },
  create(body: { name: string; domain?: string; vertical?: string; industry?: string; entity_type?: "client" | "subcontractor" | "vendor" }): Promise<{ id: string; name: string }> {
    return request(`/crm/api/companies`, { method: "POST", body: JSON.stringify(body) });
  },
  /**
   * Sparse update — only sends fields the operator changed.
   * Backend (PATCH /crm/api/companies/:id) accepts a partial body of company fields
   * including the enrichment fields (phone, street_address, hq_county, etc.).
   */
  update(id: string, body: CompanyUpdatePayload): Promise<{ ok: true }> {
    return request(`/crm/api/companies/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
};

/** Sparse update payload for PATCH /crm/api/companies/:id. */
export interface CompanyUpdatePayload {
  name?: string | null;
  domain?: string | null;
  industry?: string | null;
  vertical?: string | null;
  employee_count?: number | null;
  company_size_band?: string | null;
  annual_revenue_usd?: number | null;
  phone?: string | null;
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  description?: string | null;
  founded_year?: number | null;
  linkedin_url?: string | null;
  is_hispanic_owned?: boolean | null;
  is_in_ie?: boolean | null;
  hq_county?: string | null;
  num_locations?: number | null;
  owner_name?: string | null;
  /** Migration 0066 — flip company between client/subcontractor/vendor. */
  entity_type?: "client" | "subcontractor" | "vendor" | null;
}

// ─── Deals ────────────────────────────────────────────────────────────

export const dealsApi = {
  list(params: { pipeline_id?: string; owner?: string } = {}): Promise<{ deals: CrmDeal[]; pipelines: CrmPipeline[] }> {
    return request(`/crm/api/deals${qs(params)}`);
  },
  create(body: {
    name: string;
    pipeline_id: string;
    stage_id: string;
    amount_usd?: number;
    company_id?: string;
    primary_contact_id?: string;
    owner_user_id?: string;
    expected_close_date?: string;
    source?: string;
  }): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/deals`, { method: "POST", body: JSON.stringify(body) });
  },
  update(id: string, body: Record<string, unknown>): Promise<{ ok: true }> {
    return request(`/crm/api/deals/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  updateStage(id: string, stage_id: string): Promise<{ ok: true }> {
    return request(`/crm/api/deals/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ stage_id }) });
  },
  coach(id: string): Promise<{ coaching: string }> {
    return request(`/crm/api/deals/${encodeURIComponent(id)}/coach`, { method: "POST" });
  },
};

// ─── Contracts ────────────────────────────────────────────────────────

export const contractsApi = {
  list(params: { tab?: string; limit?: number; offset?: number } = {}): Promise<{ contracts: CrmContract[]; total: number }> {
    return request(`/crm/api/contracts${qs(params)}`);
  },
  get(id: string): Promise<CrmContract> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}`);
  },
  /**
   * Download the contract as a PDF. Fetches `/crm/contracts/:id/pdf` (worker
   * route — NOT under `/api/` because it returns a binary stream piped from
   * Cloudflare Browser Rendering, not JSON), converts the response to a
   * Blob, then triggers a synthetic <a download> click. Falls back to HTML
   * if the worker returns text/html (Browser Rendering not configured).
   *
   * Surfaced 2026-05-13: the worker route existed but (a) the SPA button had
   * no onClick, (b) the worker's catch-all middleware was 302-redirecting
   * /crm/contracts/:id/pdf to the SPA host. Both fixed.
   */
  async downloadPdf(id: string, contractName: string): Promise<void> {
    const res = await fetch(`${API_BASE}/crm/contracts/${encodeURIComponent(id)}/pdf`, {
      credentials: "include",
    });
    if (res.status === 401) {
      const fullReturn = window.location.origin + window.location.pathname + window.location.search;
      const ret = encodeURIComponent(fullReturn);
      window.location.replace(`${API_BASE}/crm/auth/login?return_to=${ret}`);
      return;
    }
    if (!res.ok) {
      throw new ApiError(res.status, await res.text().catch(() => ""));
    }
    const ct = res.headers.get("content-type") ?? "";
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Pick the extension based on what we actually got back. When CF
    // Browser Rendering is configured we get a PDF; otherwise the worker
    // falls back to HTML so the operator can print-to-PDF in the browser.
    const ext = ct.includes("pdf") ? "pdf" : "html";
    const safeName = contractName.replace(/[^a-zA-Z0-9_\- ]/g, "").trim().replace(/\s+/g, "_") || "Contract";
    a.download = `${safeName}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke after a beat so Safari has time to actually read the blob.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  templates(): Promise<{ templates: CrmContractTemplate[] }> {
    return request(`/crm/api/contracts/templates`);
  },
  clauses(): Promise<{ clauses: CrmContractClause[] }> {
    return request(`/crm/api/contracts/clauses`);
  },
  variables(): Promise<{ variables: CrmContractVariable[] }> {
    return request(`/crm/api/contracts/variables`);
  },
  // The worker expects { name, template_id }, NOT { title }. Map title→name
  // on the wire so callers can keep the friendlier "title" field.
  // parent_contract_id links a companion doc (BAA / AUP / GLBA Schedule)
  // back to the MSA it was spawned from — the GET endpoint uses that link
  // to mark the parent's companion suggestions as `already_linked`.
  create(body: {
    title: string;
    template_id?: string;
    company_id?: string;
    parent_contract_id?: string;
    primary_contact_id?: string;
  }): Promise<CrmContract> {
    return request(`/crm/api/contracts`, {
      method: "POST",
      body: JSON.stringify({
        name: body.title,
        template_id: body.template_id,
        company_id: body.company_id,
        parent_contract_id: body.parent_contract_id,
        primary_contact_id: body.primary_contact_id,
      }),
    });
  },
  update(id: string, body: Partial<CrmContract>): Promise<CrmContract> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  trash(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/trash`, { method: "POST" });
  },
  restore(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/restore`, { method: "POST" });
  },
  duplicate(id: string): Promise<CrmContract> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/duplicate`, { method: "POST" });
  },
  submitForApproval(id: string): Promise<{ ok: true; approval_id: string }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/submit-for-approval`, { method: "POST" });
  },
  approve(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/approve`, { method: "POST" });
  },
  reject(id: string, reason?: string): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/reject`, { method: "POST", body: JSON.stringify({ reason }) });
  },
  /**
   * Transition a contract through the post-sign lifecycle. Backend
   * validates the move against the state machine in
   * src/crm/contract-status.ts — returns 409 with
   * `error: "invalid_transition"` if the move isn't allowed. Sending an
   * empty `notes` is fine. `skip_email` short-circuits the client-facing
   * notification (useful for back-dated state corrections); leave it
   * unset for normal operator-driven transitions.
   */
  transitionStatus(
    id: string,
    payload: { to: string; notes?: string; skip_email?: boolean },
  ): Promise<{ ok: true; from: string; to: string }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/status`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  statusHistory(id: string): Promise<{
    history: Array<{
      from_status: string | null;
      to_status: string;
      actor_label: string | null;
      notes: string | null;
      occurred_at: string;
    }>;
  }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/status/history`);
  },
  // Line items
  addLineItem(id: string, body: Record<string, unknown>): Promise<unknown> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/line-items`, { method: "POST", body: JSON.stringify(body) });
  },
  updateLineItem(id: string, itemId: string, body: Record<string, unknown>): Promise<unknown> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/line-items/${encodeURIComponent(itemId)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  deleteLineItem(id: string, itemId: string): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/line-items/${encodeURIComponent(itemId)}`, { method: "DELETE" });
  },
  setClauses(id: string, clause_ids: string[]): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/clauses`, { method: "PUT", body: JSON.stringify({ clause_ids }) });
  },
  /** Rich-shape clause set — preserves per-clause body overrides, hide/show, notes, MCA toggle. */
  setClausesRich(
    id: string,
    clauses: Array<{
      id: string;
      body_markdown_override?: string | null;
      hidden?: boolean;
      notes?: string | null;
      mca_enabled?: boolean | null;
    }>,
  ): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/clauses`, {
      method: "PUT",
      body: JSON.stringify({ clauses }),
    });
  },
  /** Push the per-contract body_markdown_override back to the global clause library. */
  updateClauseTemplate(id: string, clauseId: string): Promise<{ ok: true }> {
    return request(
      `/crm/api/contracts/${encodeURIComponent(id)}/clauses/${encodeURIComponent(clauseId)}/update-template`,
      { method: "POST" },
    );
  },
  /**
   * Clone a contract template (master OR existing customized) into a new
   * editable template. Mirrors sowApi.cloneTemplate. The cloned template
   * shows up alongside masters in the picker so operators can spin up
   * contracts from their saved customized versions.
   */
  cloneTemplate(parent_template_id: string, name: string): Promise<{ ok: true; id: string; name: string; parent_id: string }> {
    return request(
      `/crm/api/contracts/templates/${encodeURIComponent(parent_template_id)}/clone`,
      { method: "POST", body: JSON.stringify({ name }) },
    );
  },
  /** Single template detail with version history. */
  template(id: string): Promise<{
    template: import("@/types/api").CrmContractTemplateDetail;
    versions: Array<{ version: number; change_note: string | null; created_by: string | null; created_at: string }>;
  }> {
    return request(`/crm/api/contracts/templates/${encodeURIComponent(id)}`);
  },
  /**
   * Edit a template. Body changes create a new version (kept to last 5);
   * metadata changes (name, description, tier, is_published) update in
   * place. Admin-only on the worker.
   */
  patchTemplate(id: string, body: {
    body_markdown?: string;
    fields_json?: string | null;
    change_note?: string;
    name?: string;
    description?: string | null;
    tier?: string | null;
    is_published?: boolean;
  }): Promise<{ ok: true; version?: number }> {
    return request(`/crm/api/contracts/templates/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  /** Roll back to a prior version. Implemented as a NEW version copying the target. */
  rollbackTemplate(id: string, target_version: number): Promise<{ ok: true; version: number }> {
    return request(`/crm/api/contracts/templates/${encodeURIComponent(id)}/rollback`, {
      method: "POST",
      body: JSON.stringify({ target_version }),
    });
  },
  addComponent(id: string, body: { type: string; title?: string; body?: Record<string, unknown> }): Promise<{ ok: true; id: string; position: number }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/components`, { method: "POST", body: JSON.stringify(body) });
  },
  updateComponent(id: string, componentId: string, body: { title?: string; body?: Record<string, unknown> }): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/components/${encodeURIComponent(componentId)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  deleteComponent(id: string, componentId: string): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/components/${encodeURIComponent(componentId)}`, { method: "DELETE" });
  },
  reorderComponents(id: string, ordered_ids: string[]): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/components/order`, {
      method: "PUT",
      body: JSON.stringify({ ordered_ids }),
    });
  },
  saveVariable(body: Partial<CrmContractVariable>): Promise<CrmContractVariable> {
    if (body.id) {
      return request(`/crm/api/admin/contract-variables/${encodeURIComponent(body.id)}`, { method: "PATCH", body: JSON.stringify(body) });
    }
    return request(`/crm/api/admin/contract-variables`, { method: "POST", body: JSON.stringify(body) });
  },
  deleteVariable(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/contract-variables/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};

// ─── Subcontractor onboarding pack ───────────────────────────────────
//
// Atomic 4-6 contract bundle (Subcontractor MSA + NDA + AUP + BG Check
// Disclosure + optional BAA + optional GLBA Schedule), all linked by
// a shared onboarding_pack_id. One portal invite covers the whole pack.
// Backend at src/crm/routes/onboarding-pack.ts.

export interface OnboardingPackContract {
  id: string;
  name: string;
  kind: string;
  status: string;
  template_id: string | null;
  template_name?: string | null;
  company_id?: string | null;
  primary_contact_id?: string | null;
  sent_at?: string | null;
  signed_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface OnboardingPack {
  pack_id: string;
  // Matches the GET /onboarding-pack/:pack_id response shape — company +
  // contact are nested objects, both nullable when the FK is missing.
  company: {
    id: string;
    name: string | null;
    legal_name: string | null;
    entity_type: string | null;
  } | null;
  contact: {
    id: string;
    email: string | null;
    full_name: string | null;
  } | null;
  contracts: OnboardingPackContract[];
}

export const onboardingPackApi = {
  create(body: {
    /** Null when the sub is an independent contractor (no entity). */
    company_id?: string | null;
    primary_contact_id: string;
    include_baa?: boolean;
    include_glba_schedule?: boolean;
  }): Promise<{ pack_id: string; created_contract_ids: string[] }> {
    return request(`/crm/api/contracts/onboarding-pack`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  get(packId: string): Promise<OnboardingPack> {
    return request(`/crm/api/contracts/onboarding-pack/${encodeURIComponent(packId)}`);
  },
  send(packId: string): Promise<{ ok: true; invite_id: string; sent_at: string }> {
    return request(`/crm/api/contracts/onboarding-pack/${encodeURIComponent(packId)}/send`, {
      method: "POST",
      body: JSON.stringify({ confirm: true }),
    });
  },
};

// ─── SOW ──────────────────────────────────────────────────────────────

export const sowApi = {
  get(id: string): Promise<CrmSow> {
    return request(`/crm/api/sow/${encodeURIComponent(id)}`);
  },
  patch(id: string, body: Partial<CrmSow>): Promise<CrmSow> {
    return request(`/crm/api/sow/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  // Worker expects { name, sow_template_id, ... }. Maps title→name for the
  // friendlier API + requires sow_template_id (no blank-SOW path).
  create(body: {
    kind?: string;
    name?: string;
    title?: string;
    msp_label?: string;
    sow_template_id: string;
    company_id?: string;
    effective_date?: string;
    term_months?: number;
  }): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/sow`, {
      method: "POST",
      body: JSON.stringify({
        kind: body.kind,
        name: body.name ?? body.title,
        msp_label: body.msp_label,
        sow_template_id: body.sow_template_id,
        company_id: body.company_id,
        effective_date: body.effective_date,
        term_months: body.term_months,
      }),
    });
  },
  templates(kind: string = "sow"): Promise<{ templates: Array<{ id: string; name: string; kind: string; is_master: boolean; current_version: number; description: string | null }> }> {
    return request(`/crm/api/sow/templates?kind=${kind}`);
  },
  saveTemplate(id: string, name: string): Promise<{ ok: true; template_id: string }> {
    return request(`/crm/api/sow/${encodeURIComponent(id)}/save-template`, { method: "POST", body: JSON.stringify({ name }) });
  },
  // Clone a SOW template into a new editable template. Worker requires
  // { parent_template_id, name } — different from the contract-create path.
  cloneTemplate(parent_template_id: string, name: string): Promise<{ ok: true; id: string; name: string }> {
    return request(`/crm/api/sow/clone-template`, {
      method: "POST",
      body: JSON.stringify({ parent_template_id, name }),
    });
  },
  aiReview(id: string): Promise<{ review: string }> {
    return request(`/crm/api/sow/${encodeURIComponent(id)}/ai-review`, { method: "POST" });
  },
  aiRewriteSection(id: string, sectionId: string, prompt?: string): Promise<{ rewritten: string }> {
    return request(`/crm/api/sow/${encodeURIComponent(id)}/ai-rewrite-section`, { method: "POST", body: JSON.stringify({ sectionId, prompt }) });
  },
};

// ─── Campaigns / Sequences / Templates ───────────────────────────────

import type { SequencesOverview, CampaignsOverview, SequencesQueue, SequencesSendStatus, SequencesWindowInfo } from "@/types/api";

export const campaignsApi = {
  list(): Promise<{ campaigns: CrmCampaign[] }> {
    return request(`/crm/api/campaigns`);
  },
  overview(): Promise<CampaignsOverview> {
    return request(`/crm/api/campaigns/overview`);
  },
  sequences(): Promise<{ sequences: CrmSequence[] }> {
    return request(`/crm/api/sequences`);
  },
  sequencesOverview(): Promise<SequencesOverview> {
    return request(`/crm/api/sequences/overview`);
  },
  // Cold-sequence outbox: what's queued + the live send-status pill data.
  // Optional filters: due=true (overdue only), window=24h (next 24 hours),
  // q=substring (matches email/company/vertical).
  hotFollowupQueue(): Promise<{ count: number; send_window: SequencesWindowInfo; queue: Array<{ email: string; company: string; vertical: string; subject: string; body_markdown: string; queued_at: string }> }> {
    return request(`/crm/api/sequences/hot-followup-queue`);
  },
  hotFollowupQueueUpdate(email: string, fields: { subject?: string; body_markdown?: string }): Promise<{ ok: boolean }> {
    return request(`/crm/api/sequences/hot-followup-queue/${encodeURIComponent(email)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(fields),
    });
  },
  hotFollowupQueueRemove(email: string): Promise<{ ok: boolean; remaining: number }> {
    return request(`/crm/api/sequences/hot-followup-queue/${encodeURIComponent(email)}`, {
      method: "DELETE",
    });
  },
  sequencesQueue(opts: { due?: boolean; window?: "24h"; q?: string } = {}): Promise<SequencesQueue> {
    const p = new URLSearchParams();
    if (opts.due) p.set("due", "true");
    if (opts.window) p.set("window", opts.window);
    if (opts.q) p.set("q", opts.q);
    const qs = p.toString();
    return request(`/crm/api/sequences/queue${qs ? `?${qs}` : ""}`);
  },
  sequencesSendStatus(): Promise<SequencesSendStatus> {
    return request(`/crm/api/sequences/send-status`);
  },
  sequencesSoftPause(): Promise<{ ok: true; soft_paused: true }> {
    return request(`/crm/api/sequences/soft-pause`, { method: "POST" });
  },
  sequencesSoftResume(): Promise<{
    ok: true;
    soft_paused: false;
    env_send_enabled: boolean;
    effective_paused: boolean;
  }> {
    return request(`/crm/api/sequences/soft-resume`, { method: "POST" });
  },
  // Force-send the next step for one prospect now. Respects business
  // hours + pause state; the backend will refuse with 409 outside window
  // or while paused.
  sequencesSendNow(email: string): Promise<{
    ok: true;
    email: string;
    sent: boolean;
    scheduler_result: {
      sent: number;
      skipped_window: number;
      skipped_cap: number;
      errors: string[];
    };
  }> {
    return request(`/crm/api/sequences/queue/send-now`, {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },
  // Release all due Day 0 emails immediately (bypasses send window).
  sequencesReleaseDay0(): Promise<{
    attempted: number;
    sent: number;
    skipped_window: number;
    skipped_cap: number;
    errors: string[];
    release: "day0";
  }> {
    return request(`/crm/api/sequences/release-day0`, { method: "POST" });
  },
  // Past sends with their latest outcome — powers Sent / Replied / Bounced
  // tabs on the Outbox page.
  // status accepts an array — backend filters to rows whose latest outcome
  // is in the set. Pass [] (or omit) for all outcomes.
  sequencesSent(opts: {
    status?: Array<"sent" | "opened" | "clicked" | "replied" | "bounced" | "unsubscribed">;
    limit?: number;
    q?: string;
  } = {}): Promise<{ rows: import("@/types/api").SequenceSentRow[] }> {
    const p = new URLSearchParams();
    if (opts.status && opts.status.length > 0) p.set("status", opts.status.join(","));
    if (opts.limit) p.set("limit", String(opts.limit));
    if (opts.q) p.set("q", opts.q);
    const qs = p.toString();
    return request(`/crm/api/sequences/sent${qs ? `?${qs}` : ""}`);
  },
  sequencesWhyThis(email: string): Promise<import("@/types/api").SequenceWhyThis> {
    return request(`/crm/api/sequences/why/${encodeURIComponent(email)}`);
  },
  // Resend-style detail for one past send: header + events timeline + body.
  sequencesSentDetail(email: string, sequenceId: string, step: number | null, resendEmailId?: string | null): Promise<import("@/types/api").SequenceSentDetail> {
    const p = new URLSearchParams({ email, sequence_id: sequenceId });
    if (step != null) p.set("step", String(step));
    if (resendEmailId) p.set("resend_email_id", resendEmailId);
    return request(`/crm/api/sequences/sent/detail?${p.toString()}`);
  },
  sequenceDetail(id: string): Promise<{
    id: string;
    name: string;
    vertical: string | null;
    language: string | null;
    is_active: boolean;
    total_enrolled: number;
    current_active: number;
    completed: number;
    replied: number;
    steps: Array<{
      step: number;
      day: number;
      subject: string;
      body_markdown: string;
      body_html?: string | null;
      has_override: boolean;
    }>;
  }> {
    return request(`/crm/api/sequences/${encodeURIComponent(id)}`);
  },
  templates(): Promise<{ templates: CrmEmailTemplate[] }> {
    return request(`/crm/api/email-templates`);
  },
  updateStep(seqId: string, step: number, body: { subject?: string; body_text?: string; body_markdown?: string }): Promise<{ ok: true }> {
    return request(`/crm/api/sequences/${encodeURIComponent(seqId)}/steps/${step}`, { method: "PUT", body: JSON.stringify(body) });
  },
  deleteStep(seqId: string, step: number): Promise<{ ok: true }> {
    return request(`/crm/api/sequences/${encodeURIComponent(seqId)}/steps/${step}`, { method: "DELETE" });
  },
  insights(): Promise<{ insights: string }> {
    return request(`/crm/api/ai/campaign-insights`, { method: "POST" });
  },
};

// ─── Tasks ────────────────────────────────────────────────────────────

export const tasksApi = {
  list(params: { tab?: "open" | "completed" | "overdue"; owner?: string } = {}): Promise<{ tasks: CrmTask[] }> {
    return request(`/crm/api/tasks${qs(params)}`);
  },
  create(body: Partial<CrmTask>): Promise<CrmTask> {
    return request(`/crm/api/tasks`, { method: "POST", body: JSON.stringify(body) });
  },
  complete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/tasks/${encodeURIComponent(id)}/complete`, { method: "POST" });
  },
  reopen(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/tasks/${encodeURIComponent(id)}/reopen`, { method: "POST" });
  },
  update(id: string, body: Partial<CrmTask>): Promise<CrmTask> {
    return request(`/crm/api/tasks/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  delete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/tasks/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};


// ─── Reports ──────────────────────────────────────────────────────────

export const reportsApi = {
  summary(): Promise<ReportsSummary> {
    return request(`/crm/api/reports/summary`);
  },

  // ─── GA4 Website Performance ───────────────────────────────────────
  // Server returns { ok:false, error:"ga_not_configured" } with HTTP 503
  // when secrets/property aren't wired yet. Callers handle that gracefully.

  analyticsRealtime(): Promise<GaRealtimeResponse> {
    return request(`/crm/api/analytics/realtime`);
  },
  analyticsOverview(window: GaWindow): Promise<GaOverviewResponse> {
    return request(`/crm/api/analytics/overview?window=${encodeURIComponent(window)}`);
  },
  analyticsTopPages(window: GaWindow, limit = 20): Promise<GaTopPagesResponse> {
    return request(`/crm/api/analytics/top-pages?window=${encodeURIComponent(window)}&limit=${limit}`);
  },
  analyticsSources(window: GaWindow): Promise<GaSourcesResponse> {
    return request(`/crm/api/analytics/sources?window=${encodeURIComponent(window)}`);
  },
  analyticsVerticals(window: GaWindow): Promise<GaTopPagesResponse> {
    return request(`/crm/api/analytics/verticals?window=${encodeURIComponent(window)}`);
  },
  analyticsEngagement(window: GaWindow, minViews = 10): Promise<GaEngagementResponse> {
    return request(`/crm/api/analytics/engagement?window=${encodeURIComponent(window)}&min_views=${minViews}`);
  },

  // ─── Email engagement quality (bot-filter health) ─────────────────────
  // Companion to GA Website Performance, but on the OUTBOUND side:
  // what % of clicks/opens we caught as scanners vs real humans. Sourced
  // from email_events (no GA dependency). Healthy band 5-30%.
  botStats(window: BotStatsWindow = "24h"): Promise<BotStatsResponse> {
    return request(`/crm/api/clicks/bot-stats?window=${encodeURIComponent(window)}`);
  },

  // ─── Google OAuth (delegated permissions, like MS Graph) ───────────
  // The Connect button uses window.location.assign(googleOauthStartUrl())
  // because Google's authorize endpoint needs a TOP-LEVEL redirect — XHR/
  // fetch can't follow it. status + disconnect are normal JSON calls.
  //
  // CRITICAL: pass the FULL absolute URL of where the operator is now,
  // not just the path. The OAuth callback runs on the worker (api.*) and
  // resolves a path-only return_to against the worker's origin — landing
  // the operator on api.luzgridtech.com/reports (which is wrong, the SPA
  // lives on crm.luzgridtech.com). Sending window.location.origin + path
  // closes that loop. The worker validates the URL against luzgridtech.com.
  googleOauthStartUrl(returnPath: string = "/reports"): string {
    const fullReturn = (typeof window !== "undefined" ? window.location.origin : "") + returnPath;
    return `/crm/api/analytics/google-oauth/start?return_to=${encodeURIComponent(fullReturn)}`;
  },
  googleOauthStatus(): Promise<GoogleOauthStatus> {
    return request(`/crm/api/analytics/google-oauth/status`);
  },
  googleOauthDisconnect(): Promise<{ ok: true; fallback: string }> {
    return request(`/crm/api/analytics/google-oauth/disconnect`, { method: "POST" });
  },
};

export interface GoogleOauthStatus {
  ok: boolean;
  configured: boolean;
  connected: boolean;
  meta?: {
    connected_at: string;
    connected_by_crm_user?: string;
    google_email?: string;
    scope?: string;
  } | null;
}

// ─── GA Website Performance types ──────────────────────────────────────

/** Time window selector for the Website Traffic section.
 *  2026-05-13: switched from "today | 5d | 7d | 30d" to operator-preferred
 *  "1d | 5d | 10d | 30d". The worker's rangeForKey() honors both old + new
 *  keys for back-compat, but new code should emit only the new keys. */
export type GaWindow = "1d" | "5d" | "10d" | "30d";

export interface GaErrorPayload {
  ok: false;
  error: "ga_not_configured" | "ga_api_error" | "internal";
  missing?: string[];
  upstream_status?: number;
  body?: unknown;
  message?: string;
}

export interface GaRealtimeResponse {
  ok: true;
  active_users: number;
  top_pages: Array<Record<string, string | number>>;
  top_countries: Array<Record<string, string | number>>;
  generated_at: string;
}

export interface GaOverviewResponse {
  ok: true;
  window: GaWindow;
  totalUsers: number;
  sessions: number;
  screenPageViews: number;
  averageSessionDuration: number;
  bounceRate: number;
  engagedSessions: number;
  engagementRate: number;
  prior: {
    totalUsers: number;
    sessions: number;
    screenPageViews: number;
    averageSessionDuration: number;
    bounceRate: number;
    engagedSessions: number;
    engagementRate: number;
  };
}

export interface GaTopPagesResponse {
  ok: true;
  window: GaWindow;
  rows: Array<Record<string, string | number>>;
}

export interface GaSourcesResponse {
  ok: true;
  window: GaWindow;
  by_channel: Array<Record<string, string | number>>;
  by_source: Array<Record<string, string | number>>;
}

export interface GaEngagementResponse {
  ok: true;
  window: GaWindow;
  min_views: number;
  stickiest: Array<Record<string, string | number>>;
  problems: Array<Record<string, string | number>>;
}

// ─── Email engagement quality (bot filter) ───────────────────────────
// Window keys here are independent from GaWindow — bot-stats uses
// short tactical windows (1h/24h) that map directly onto operator-monitor
// cadence, not the 30-day GA reporting cadence.

export type BotStatsWindow = "1h" | "24h" | "7d" | "30d";

export type BotStatsHealth =
  | "no_data_in_window"
  | "filter_caught_zero"
  | "filter_alarming_high"
  | "healthy";

export interface BotStatsBucket {
  human: number;
  bot: number;
  total: number;
  bot_share_pct: number;
}

export interface BotStatsRecentEvent {
  prospect_email: string;
  event_type: "clicked_bot" | "opened_bot" | string;
  clicked_url: string | null;
  sequence_id: string | null;
  step: number | null;
  user_agent: string | null;
  ip_address: string | null;
  created_at: string;
}

export interface BotStatsResponse {
  ok: true;
  window: BotStatsWindow;
  clicks: BotStatsBucket;
  opens: BotStatsBucket;
  overall_bot_share_pct: number;
  recent_bot_events: BotStatsRecentEvent[];
  top_scanner_uas: Array<{ user_agent: string; n: number }>;
  health: BotStatsHealth;
  generated_at: string;
}

// ─── Home dashboard + current user ────────────────────────────────────

import type { HomeData, CurrentUser } from "@/types/api";

export const homeApi = {
  get(): Promise<HomeData> {
    return request(`/crm/api/home`);
  },
};

export const meApi = {
  get(): Promise<CurrentUser> {
    return request(`/crm/api/me`);
  },
};

// ─── Forms / Suppressions / Privacy ───────────────────────────────────

export interface FormField {
  name: string;
  label: string;
  type: "text" | "email" | "phone" | "textarea" | "select";
  required?: boolean;
  options?: string[];
  placeholder?: string;
}

export interface FormSubmission {
  id: string;
  contact_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  values: Record<string, unknown>;
  submitted_at: string;
  is_spam: boolean;
  processed: boolean;
}

export interface FormDetail {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  fields: FormField[];
  submit_button_label: string | null;
  success_message_markdown: string | null;
  success_redirect_url: string | null;
  create_or_update_contact: boolean;
  auto_enroll_workflow_id: string | null;
  notify_emails: string[];
  is_active: boolean;
  views_count: number;
  submissions_count: number;
  conversion_rate: number;
  created_at: string;
  updated_at: string;
  submissions: FormSubmission[];
}

// Saved views — named (filters, columns, sort) tuples scoped to an object_type.
export interface SavedView {
  id: string;
  object_type: string;
  name: string;
  owner_user_id: string | null;
  is_shared: boolean;
  is_default: boolean;
  filters: Record<string, unknown>;
  columns: string[];
  sort: { key: string; dir: "asc" | "desc" } | null;
  created_at: string;
  updated_at: string;
}
export const savedViewsApi = {
  list(objectType: string): Promise<{ views: SavedView[] }> {
    return request(`/crm/api/saved-views?object_type=${encodeURIComponent(objectType)}`);
  },
  create(body: {
    object_type: string;
    name: string;
    is_shared?: boolean;
    is_default?: boolean;
    filters?: unknown;
    columns?: string[];
    sort?: { key: string; dir: "asc" | "desc" } | null;
  }): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/saved-views`, { method: "POST", body: JSON.stringify(body) });
  },
  update(id: string, patch: Partial<Omit<SavedView, "id" | "object_type" | "owner_user_id" | "created_at" | "updated_at">>): Promise<{ ok: true }> {
    return request(`/crm/api/saved-views/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  delete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/saved-views/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};

export const formsApi = {
  list(): Promise<{ forms: CrmForm[] }> {
    return request(`/crm/api/forms`);
  },
  get(id: string): Promise<FormDetail> {
    return request(`/crm/api/forms/${encodeURIComponent(id)}`);
  },
  create(body: { name: string; description?: string }): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/forms`, { method: "POST", body: JSON.stringify(body) });
  },
  update(id: string, patch: Partial<Omit<FormDetail, "id" | "submissions" | "created_at" | "updated_at" | "views_count" | "submissions_count" | "conversion_rate" | "slug">>): Promise<{ ok: true }> {
    return request(`/crm/api/forms/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  delete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/forms/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  suppressions(): Promise<{ suppressions: CrmSuppression[] }> {
    return request(`/crm/api/admin/suppressions`);
  },
  addSuppression(body: { email: string; reason: string }): Promise<{ ok: true }> {
    return request(`/crm/api/admin/suppressions`, { method: "POST", body: JSON.stringify(body) });
  },
  removeSuppression(email: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/suppressions/${encodeURIComponent(email)}`, { method: "DELETE" });
  },
};

export interface FormBlocklistEntry {
  id: number;
  domain: string;
  reason: string | null;
  added_by_user_id: string | null;
  added_by_name: string | null;
  added_at: string;
}

export const formBlocklistApi = {
  list(): Promise<{
    entries: FormBlocklistEntry[];
    baseline: string[];
    env_var: string[];
  }> {
    return request(`/crm/api/admin/form-blocklist`);
  },
  add(body: { domain: string; reason?: string }): Promise<{ ok: true; domain: string }> {
    return request(`/crm/api/admin/form-blocklist`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  remove(id: number): Promise<{ ok: true; deleted: number }> {
    return request(`/crm/api/admin/form-blocklist/${id}`, { method: "DELETE" });
  },
};

export const botDefensesApi = {
  get(): Promise<{
    enabled: boolean;
    source: "db" | "env_var_fallback";
    updated_at: string | null;
    updated_by: string | null;
  }> {
    return request(`/crm/api/admin/settings/bot-defenses`);
  },
  set(enabled: boolean): Promise<{ ok: true; enabled: boolean }> {
    return request(`/crm/api/admin/settings/bot-defenses`, {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    });
  },
};

export const privacyApi = {
  list(params: { tab?: string } = {}): Promise<{ requests: CrmPrivacyRequest[] }> {
    return request(`/crm/api/admin/privacy/requests${qs(params)}`);
  },
  get(id: string): Promise<CrmPrivacyRequest> {
    return request(`/crm/api/admin/privacy/requests/${encodeURIComponent(id)}`);
  },
  verify(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/privacy/${encodeURIComponent(id)}/verify`, { method: "POST" });
  },
  execute(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/privacy/${encodeURIComponent(id)}/execute`, { method: "POST" });
  },
  exportData(id: string): Promise<{ export_url: string }> {
    return request(`/crm/api/admin/privacy/${encodeURIComponent(id)}/export`, { method: "POST" });
  },
  complete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/privacy/${encodeURIComponent(id)}/complete`, { method: "POST" });
  },
  deny(id: string, denial_reason: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/privacy/${encodeURIComponent(id)}/deny`, { method: "POST", body: JSON.stringify({ denial_reason }) });
  },
};

// ─── Admin ────────────────────────────────────────────────────────────

export const adminApi = {
  users(): Promise<{ users: CrmUser[] }> {
    return request(`/crm/api/admin/users`);
  },
  expiry(): Promise<import("@/types/api").AdminExpiryResponse> {
    return request(`/crm/api/admin/expiry`);
  },
  audit(params: { limit?: number; offset?: number } = {}): Promise<{ entries: CrmAuditEntry[] }> {
    return request(`/crm/api/admin/audit${qs(params)}`);
  },
  costs(): Promise<import("@/types/api").AdminCostsResponse> {
    return request(`/crm/api/admin/costs`);
  },
  refreshCosts(): Promise<{ updated: boolean }> {
    return request(`/crm/api/admin/costs/refresh`, { method: "POST" });
  },
  costAlertCreate(body: Partial<CrmCostAlert>): Promise<CrmCostAlert> {
    return request(`/crm/api/admin/cost-alerts`, { method: "POST", body: JSON.stringify(body) });
  },
  costAlertToggle(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/cost-alerts/${encodeURIComponent(id)}/toggle`, { method: "POST" });
  },
  costAlertDelete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/cost-alerts/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  aiSettings(): Promise<CrmAiSettings> {
    return request(`/crm/api/admin/ai/settings`);
  },
  aiSettingsUpdate(body: Partial<CrmAiSettings>): Promise<{ ok: true }> {
    return request(`/crm/api/admin/ai/settings`, { method: "POST", body: JSON.stringify(body) });
  },
  apiKeys(): Promise<{ keys: CrmApiKey[] }> {
    return request(`/crm/api/admin/api-keys`);
  },
  apiKeyCreate(body: { name: string; scope: string }): Promise<{ key: string; id: string }> {
    return request(`/crm/api/admin/api-keys`, { method: "POST", body: JSON.stringify(body) });
  },
  apiKeyRevoke(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/api-keys/${encodeURIComponent(id)}/revoke`, { method: "POST" });
  },
  branding(): Promise<CrmBranding> {
    return request(`/crm/api/admin/branding`);
  },
  brandingUpdate(body: Partial<CrmBranding>): Promise<{ ok: true }> {
    return request(`/crm/api/admin/branding`, { method: "POST", body: JSON.stringify(body) });
  },
  diagnose(what: string): Promise<{ ok: boolean; result?: unknown; error?: string }> {
    return request(`/crm/api/admin/diagnose/${encodeURIComponent(what)}`);
  },
  diagnoseAll(): Promise<Record<string, { ok: boolean; result?: unknown; error?: string }>> {
    return request(`/crm/api/admin/diagnose`);
  },
  integrations(): Promise<{ integrations: Array<{ name: string; status: string; details?: string }> }> {
    return request(`/crm/api/admin/integrations`);
  },
};

// ─── Workflows + Lists ────────────────────────────────────────────────

import type { CrmWorkflow, CrmList, CrmListDetail } from "@/types/api";

export const workflowsApi = {
  list(params: { status?: string } = {}): Promise<{ workflows: CrmWorkflow[] }> {
    return request(`/crm/api/workflows${qs(params)}`);
  },
  get(id: string): Promise<CrmWorkflow> {
    return request(`/crm/api/workflows/${encodeURIComponent(id)}`);
  },
  create(body: Partial<CrmWorkflow>): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/workflows`, { method: "POST", body: JSON.stringify(body) });
  },
  update(id: string, body: Partial<CrmWorkflow>): Promise<{ ok: true }> {
    return request(`/crm/api/workflows/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  archive(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/workflows/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  aiDraft(prompt: string): Promise<{ ok: true; draft: Partial<CrmWorkflow> }> {
    return request(`/crm/api/workflows/draft`, {
      method: "POST",
      body: JSON.stringify({ prompt }),
    });
  },
  enroll(id: string, contactId: string): Promise<{ ok: true; run_id: string }> {
    return request(`/crm/api/workflows/${encodeURIComponent(id)}/enroll`, {
      method: "POST",
      body: JSON.stringify({ contact_id: contactId }),
    });
  },
};

export const listsApi = {
  list(): Promise<{ lists: CrmList[] }> {
    return request(`/crm/api/lists`);
  },
  get(id: string): Promise<CrmListDetail> {
    return request(`/crm/api/lists/${encodeURIComponent(id)}`);
  },
  create(body: Partial<CrmList>): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/lists`, { method: "POST", body: JSON.stringify(body) });
  },
  update(id: string, body: Partial<CrmList>): Promise<{ ok: true }> {
    return request(`/crm/api/lists/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  delete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/lists/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  addMember(id: string, contactId: string): Promise<{ ok: true }> {
    return request(`/crm/api/lists/${encodeURIComponent(id)}/members`, {
      method: "POST",
      body: JSON.stringify({ contact_id: contactId }),
    });
  },
  recompute(id: string): Promise<{ ok: true; member_count: number }> {
    return request(`/crm/api/lists/${encodeURIComponent(id)}/recompute`, {
      method: "POST",
    });
  },
};

// Client portal — staff side. Public portal SPA hits a different endpoint.
export interface ProjectTemplate {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  is_active: boolean;
  stages: Array<{
    id: string;
    position: number;
    label: string;
    description: string | null;
    icon: string | null;
    required_action: string | null;
  }>;
}

export interface CrmProject {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  status: "active" | "completed" | "archived" | "on_hold";
  template_id: string | null;
  current_stage_id: string | null;
  company_id: string | null;
  company_name: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  stage_count: number;
  completed_stages: number;
  stakeholder_count: number;
}

export interface CrmProjectStage {
  id: string;
  project_id: string;
  position: number;
  label: string;
  description: string | null;
  icon: string | null;
  required_action: string | null;
  contract_id: string | null;
  form_id: string | null;
  file_id: string | null;
  status: "pending" | "active" | "completed" | "skipped";
  completed_at: string | null;
}

export interface CrmProjectStakeholder {
  contact_id: string;
  email: string;
  full_name: string;
  role: string | null;
  invited_at: string;
  first_seen_at: string | null;
}

export const projectsApi = {
  templates(): Promise<{ templates: ProjectTemplate[] }> {
    return request(`/crm/api/project-templates`);
  },
  list(params: { status?: string; company_id?: string } = {}): Promise<{ projects: CrmProject[] }> {
    return request(`/crm/api/projects${qs(params)}`);
  },
  get(idOrSlug: string): Promise<{
    project: CrmProject;
    stages: CrmProjectStage[];
    stakeholders: CrmProjectStakeholder[];
    tasks: Array<Record<string, unknown>>;
  }> {
    return request(`/crm/api/projects/${encodeURIComponent(idOrSlug)}`);
  },
  create(body: {
    name: string;
    description?: string;
    company_id: string;
    template_id?: string;
    stakeholder_contact_ids?: string[];
    send_invites?: boolean;
  }): Promise<{ ok: true; id: string; slug: string; invites_sent: number; invite_errors: { email: string; error: string }[] }> {
    return request(`/crm/api/projects`, { method: "POST", body: JSON.stringify(body) });
  },
  update(id: string, patch: Partial<{ name: string; description: string; status: string; owner_user_id: string }>): Promise<{ ok: true }> {
    return request(`/crm/api/projects/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  // Hard delete — admin only. Cascades all subtables (stages, stakeholders,
  // tasks, files, comments, integrations, portal invite tokens). Active
  // projects require force=true (small speedbump, not a security gate).
  delete(id: string, opts: { force?: boolean } = {}): Promise<{ ok: true; deleted_project_id: string; deleted_project_name: string; deleted_project_slug: string }> {
    const qs = opts.force ? "?force=1" : "";
    return request(`/crm/api/projects/${encodeURIComponent(id)}${qs}`, { method: "DELETE" });
  },
  invite(id: string, contactIds?: string[]): Promise<{ ok: true; sent: number; errors: { email: string; error: string }[] }> {
    return request(`/crm/api/projects/${encodeURIComponent(id)}/invite`, {
      method: "POST",
      body: JSON.stringify({ contact_ids: contactIds ?? [] }),
    });
  },
  addStakeholder(id: string, contactId: string, role?: string, sendInvite = true): Promise<{ ok: true; invite_sent?: boolean; errors?: { email: string; error: string }[] }> {
    return request(`/crm/api/projects/${encodeURIComponent(id)}/stakeholders`, {
      method: "POST",
      body: JSON.stringify({ contact_id: contactId, role, send_invite: sendInvite }),
    });
  },
  createTask(id: string, body: {
    title: string;
    description_markdown?: string;
    stage_id?: string;
    assigned_to_contact_id?: string;
    assigned_to_user_id?: string;
    due_at?: string;
  }): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/projects/${encodeURIComponent(id)}/tasks`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
};

// ─── Search ───────────────────────────────────────────────────────────

// ─── Admin integrations ───────────────────────────────────────────────

export interface IntegrationSecretStatus {
  key: string;
  set: boolean;
  masked: string | null;        // e.g. "●●●●●●●●●●1234" or null when unset
  set_at: string | null;
  last_used_at: string | null;
  required: boolean;
}

export interface Integration {
  integration_id: string;
  display_name: string;
  description: string;
  category: string;
  docs_url: string | null;
  required_secret_keys: string[];
  optional_secret_keys: string[];
  setup_url: string | null;
  display_order: number;
  secrets: IntegrationSecretStatus[];
  configured: boolean;          // all required keys are set
}

export const integrationsApi = {
  list(): Promise<{ integrations: Integration[]; has_master_key: boolean }> {
    return request("/crm/api/admin/integrations");
  },
  setSecret(key: string, value: string, label?: string, description?: string): Promise<{
    ok: true; key: string; masked: string; rotated: boolean;
  }> {
    return request(`/crm/api/admin/integrations/secrets/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: JSON.stringify({ value, label, description }),
    });
  },
  clearSecret(key: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/integrations/secrets/${encodeURIComponent(key)}`, {
      method: "DELETE",
    });
  },
};

// ─── Portal invites ───────────────────────────────────────────────────

export interface PortalInviteRow {
  id: number;
  email_sent_to: string;
  language: "en" | "es";
  created_by: string | null;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
  accepted_subject: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  resent_count: number;
  last_resent_at: string | null;
  status: "pending" | "accepted" | "revoked" | "expired";
}

export const portalInvitesApi = {
  list(contactId: string): Promise<{ rows: PortalInviteRow[] }> {
    return request(`/crm/api/contacts/${encodeURIComponent(contactId)}/portal-invites`);
  },
  create(contactId: string, body: { language?: "en" | "es" } = {}): Promise<{
    ok: true; invite_id: number | null; email_sent_to: string;
    language: string; expires_at: string;
    email_send_ok: boolean; email_send_error?: string;
    reused?: boolean;
  }> {
    return request(`/crm/api/contacts/${encodeURIComponent(contactId)}/portal-invite`, {
      method: "POST", body: JSON.stringify(body),
    });
  },
  /** Generate a NEW token + extend expiry + re-email. Old link stops working. */
  regenerate(inviteId: number): Promise<{ ok: true; email_send_ok: boolean; expires_at: string }> {
    return request(`/crm/api/portal-invites/${inviteId}/regenerate`, { method: "POST" });
  },
  revoke(inviteId: number): Promise<{ ok: true }> {
    return request(`/crm/api/portal-invites/${inviteId}`, { method: "DELETE" });
  },
};

// ─── Cascade delete ───────────────────────────────────────────────────
//
// Generic "delete this entity + its children" flow. Backed by
// /crm/api/_cascade/:entity/:id/{preview,delete} on the worker.

export interface CascadeChild {
  table: string;
  label: string;
  count: number;
  detach_only: boolean;
}
export interface CascadeSuppression {
  email: string;
  reason: string;
  source: string | null;
  notes: string | null;
  added_at: string;
}

export interface CascadePreview {
  entity: string;
  id: string;
  name: string;
  children: CascadeChild[];
  /** Contact-only: present when the contact's email has a crm_suppressions
   *  row. Surfaced separately from children because suppressions are
   *  immutable per the audit rule — clearing requires a reason and is
   *  audit-logged. */
  suppression?: CascadeSuppression | null;
}

export type CascadeEntity = "contract" | "deal" | "company" | "contact" | "task";

export interface CascadeDeleteResult {
  ok: true;
  deleted: Record<string, number>;
  detached: Record<string, number>;
  name: string;
  restore_id: number | null;
  expires_at: string;
  /** True when the operator opted in to clear_suppression and a row
   *  was actually removed (audit-logged). */
  suppression_cleared?: boolean;
}

/** Optional opt-in to clear the contact's suppression as part of the
 *  delete. Reason is required for the audit log; actor is derived from
 *  the CRM session server-side. */
export interface CascadeClearSuppression {
  reason: string;
}

export interface TrashRow {
  id: number;
  entity: string;
  entity_id: string;
  display_name: string | null;
  deleted_by: string | null;
  deleted_at: string;
  expires_at: string;
  detach_log_json: string | null;
}

export const cascadeApi = {
  preview(entity: CascadeEntity, id: string): Promise<CascadePreview> {
    return request(`/crm/api/_cascade/${entity}/${encodeURIComponent(id)}/preview`);
  },
  /**
   * `children` is a map { tableName: true } selecting which child sets
   * to ALSO delete (or detach, for detach_only children). Tables not
   * present default to skip — but detach_only children always get
   * detached server-side regardless, otherwise we'd leave dangling FKs.
   *
   * Returns restore_id which can be passed to trashApi.restore() to undo
   * within 90 days. After 90 days the daily cron auto-purges.
   */
  delete(
    entity: CascadeEntity,
    id: string,
    children: Record<string, boolean>,
    clearSuppression?: CascadeClearSuppression,
  ): Promise<CascadeDeleteResult> {
    return request(`/crm/api/_cascade/${entity}/${encodeURIComponent(id)}/delete`, {
      method: "POST",
      body: JSON.stringify({
        children,
        ...(clearSuppression ? { clear_suppression: clearSuppression } : {}),
      }),
    });
  },
};

export const trashApi = {
  list(opts?: { entity?: CascadeEntity | string; limit?: number } | CascadeEntity): Promise<{ rows: TrashRow[] }> {
    // Tolerate both the legacy single-entity call (`trashApi.list("contact")`)
    // and the newer object form (`trashApi.list({ entity, limit })`).
    const p = new URLSearchParams();
    if (typeof opts === "string") {
      p.set("entity", opts);
    } else if (opts) {
      if (opts.entity) p.set("entity", opts.entity);
      if (opts.limit) p.set("limit", String(opts.limit));
    }
    const qs = p.toString();
    return request(`/crm/api/_trash${qs ? `?${qs}` : ""}`);
  },
  restore(trashId: number): Promise<{ ok: true; entity: string; entity_id: string; restored: Record<string, number> }> {
    return request(`/crm/api/_trash/${trashId}/restore`, { method: "POST" });
  },
};

export const searchApi = {
  global(q: string): Promise<{ contacts: CrmContact[]; companies: CrmCompany[]; deals: CrmDeal[] }> {
    return request(`/crm/api/search?q=${encodeURIComponent(q)}`);
  },
  ai(q: string): Promise<{ result: string; matches?: unknown[] }> {
    return request(`/crm/api/ai/search`, { method: "POST", body: JSON.stringify({ q }) });
  },
};

// ─── Pricing calculator config (Part 5) ───────────────────────────────
// Editable, backend-persisted tier presets + business defaults so a vendor
// price change (e.g. Keeper $3.00 → $3.40) is done once in the app and
// reused by every future quote. Admin-only on the worker side.

export type PresetCostType =
  | "per_user" | "per_device" | "per_server" | "per_workstation" | "per_company" | "flat";
export type PresetLaborBasis =
  | "per_user" | "per_device" | "per_workstation" | "per_server" | "flat";

export interface PricingPresetService {
  name: string;
  cost: number;
  type: PresetCostType;
  passThrough?: boolean;
  resalePrice?: number;
}
export interface PricingPresetLabor {
  name: string;
  rate: number;
  hours: number;
  basis: PresetLaborBasis;
}
export interface PricingTierPreset {
  suggestedListPricePerUser: number;
  onboarding: { mode: "per_seat" | "flat"; amount: number };
  services: PricingPresetService[];
  labor: PricingPresetLabor[];
}
export interface PricingTierPresets {
  foundation: PricingTierPreset;
  pro: PricingTierPreset;
  enterprise: PricingTierPreset;
}

export interface PricingOverheadItem {
  id: string;
  name: string;
  monthlyCost: number;
  kind: "fixed" | "per_tech" | "percent_of_revenue";
  percent?: number;
}
export interface PricingBusinessDefaults {
  numTechs: number;
  overheadAllocation: "even_per_client" | "per_seat";
  bundleM365: boolean;
  laborIsCogs: boolean;
  defaultMarginPct: number;
  defaultNetMarginTarget: number;
  defaultAnnualEscalatorPct: number;
  defaultBillRate: number;
  overhead: PricingOverheadItem[];
}

export const pricingApi = {
  presets(): Promise<{
    ok: true; presets: PricingTierPresets; defaults: PricingTierPresets;
    updated_at: string | null; updated_by_user_id: string | null; seeded: boolean;
  }> {
    return request(`/crm/api/pricing/presets`);
  },
  putPresets(presets: PricingTierPresets): Promise<{ ok: true }> {
    return request(`/crm/api/pricing/presets`, { method: "PUT", body: JSON.stringify({ presets }) });
  },
  resetPresets(): Promise<{ ok: true; presets: PricingTierPresets }> {
    return request(`/crm/api/pricing/presets/reset`, { method: "POST" });
  },
  businessDefaults(): Promise<{
    ok: true; defaults: PricingBusinessDefaults; systemDefaults: PricingBusinessDefaults;
    updated_at: string | null; updated_by_user_id: string | null; seeded: boolean;
  }> {
    return request(`/crm/api/pricing/business-defaults`);
  },
  putBusinessDefaults(defaults: PricingBusinessDefaults): Promise<{ ok: true }> {
    return request(`/crm/api/pricing/business-defaults`, { method: "PUT", body: JSON.stringify({ defaults }) });
  },
  resetBusinessDefaults(): Promise<{ ok: true; defaults: PricingBusinessDefaults }> {
    return request(`/crm/api/pricing/business-defaults/reset`, { method: "POST" });
  },
};

// ─── Scheduled / draft emails ─────────────────────────────────────────

export interface ScheduledEmail {
  id: string;
  contact_id: string;
  thread_id: string | null;
  to_email: string;
  from_mailbox: string;
  subject: string;
  body_text: string;
  include_signature: number;
  status: "draft" | "scheduled" | "sent" | "canceled" | "failed";
  scheduled_for: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  send_error: string | null;
}

export const scheduledEmailApi = {
  list(contactId: string): Promise<{ scheduled_emails: ScheduledEmail[] }> {
    return request(`/crm/api/contacts/${encodeURIComponent(contactId)}/scheduled-emails`);
  },
  create(
    contactId: string,
    payload: { subject?: string; body: string; to?: string; from_mailbox?: string; include_signature?: boolean },
  ): Promise<{ ok: true; scheduled_email: ScheduledEmail }> {
    return request(`/crm/api/contacts/${encodeURIComponent(contactId)}/scheduled-email`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  update(
    id: string,
    payload: { subject?: string; body?: string; from_mailbox?: string; include_signature?: boolean },
  ): Promise<{ ok: true; scheduled_email: ScheduledEmail }> {
    return request(`/crm/api/scheduled-emails/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  // Window-aware: returns action "sent" if fired now, or "scheduled" with
  // scheduled_for when queued for the next window open.
  send(id: string): Promise<{ ok: true; action: "sent" | "scheduled"; scheduled_for?: string; scheduled_email: ScheduledEmail }> {
    return request(`/crm/api/scheduled-emails/${encodeURIComponent(id)}/send`, { method: "POST" });
  },
  // Force-send a queued row now, ignoring the window.
  release(id: string): Promise<{ ok: true; action: "sent"; scheduled_email: ScheduledEmail }> {
    return request(`/crm/api/scheduled-emails/${encodeURIComponent(id)}/release`, { method: "POST" });
  },
  cancel(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/scheduled-emails/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};

// ─── Auth ─────────────────────────────────────────────────────────────

export const authApi = {
  // The worker doesn't currently expose a "/me" JSON endpoint; we infer
  // session validity by hitting any auth-gated endpoint and watching for 401.
  // /api/notifications is cheap and already exists.
  async ping(): Promise<boolean> {
    try {
      await notificationsApi.list();
      return true;
    } catch {
      return false;
    }
  },
};
